import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const navItems = [
  { to: '/admin', label: 'Overview', icon: '📊', end: true },
  { to: '/admin/homepage', label: 'Homepage', icon: '🏠' },
  { to: '/admin/about-page', label: 'About Us', icon: '📜' },
  { to: '/admin/services-page', label: 'Services', icon: '🧾' },
  { to: '/admin/applications-page', label: 'Applications', icon: '🧩' },
  { to: '/admin/applications-catalog', label: 'App Catalog', icon: '📱', superAdmin: true },
  { to: '/admin/bios', label: 'Bios', icon: '🧑‍💼' },
  { to: '/admin/partners', label: 'Partners', icon: '🤝' },
  { to: '/admin/media-library', label: 'Media library', icon: '🖼️', superAdmin: true },
  { to: '/admin/users', label: 'People', icon: '👥' },
  { to: '/admin/roles', label: 'Roles', icon: '🛡️' },
  { to: '/admin/programming', label: 'Programming', icon: '📺', superAdmin: true },
  { to: '/admin/program-library', label: 'Program library', icon: '📚', superAdmin: true },
  { to: '/admin/playback-monitor', label: 'Playback monitor', icon: '🎚️', superAdmin: true },
];

const AdminLayout = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const visibleNavItems = navItems.filter((item) => !item.superAdmin || isSuperAdmin());
  const [serverOffsetMs, setServerOffsetMs] = useState(null);
  const [serverTimezone, setServerTimezone] = useState('');
  const [displayTime, setDisplayTime] = useState(new Date());

  const formatServerTime = (timestamp, timezone) => {
    if (!timestamp) return '--:--';
    const options = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    };
    if (timezone) {
      options.timeZone = timezone;
    }
    return new Date(timestamp).toLocaleString([], options);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchServerTime = async () => {
      try {
        const { data } = await axios.get('/admin/server-time');
        if (!isMounted) return;
        const serverNow = new Date(data.server_time).getTime();
        if (!Number.isNaN(serverNow)) {
          setServerOffsetMs(serverNow - Date.now());
        }
        setServerTimezone(data.server_timezone || '');
      } catch (error) {
        if (!isMounted) return;
      }
    };

    fetchServerTime();
    const timer = setInterval(fetchServerTime, 10000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (serverOffsetMs == null) {
        setDisplayTime(new Date());
        return;
      }
      setDisplayTime(new Date(Date.now() + serverOffsetMs));
    }, 1000);

    return () => clearInterval(timer);
  }, [serverOffsetMs]);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div>
          <div className="sidebar-brand">HEM Command</div>
          <p className="text-muted" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Control every environment from one surface.
          </p>
          <div className="text-muted" style={{ color: 'rgba(255,255,255,0.7)', marginTop: '0.75rem' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Server time
            </div>
            <div style={{ fontWeight: 600, marginTop: '0.25rem' }}>
              {formatServerTime(displayTime, serverTimezone)}
            </div>
            {serverTimezone && (
              <div style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
                {serverTimezone}
              </div>
            )}
          </div>
        </div>

        <nav className="nav-list">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                ['nav-link', isActive ? 'active' : null].filter(Boolean).join(' ')
              }
            >
              <span className="icon" aria-hidden="true">
                {item.icon}
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-user">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0) ?? 'A'}
            </div>
            <div>
              <h4>{user?.name}</h4>
              <p>{user?.roles?.[0]?.slug ?? 'user'}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            className="btn btn-muted"
            style={{ width: '100%', marginTop: '1rem' }}
          >
            Log out
          </button>
        </div>
      </aside>

      <div className="content-shell">
        <header className="topbar">
          <div className="page-heading">
            <p className="text-muted" style={{ marginBottom: '0.35rem' }}>
              Welcome back
            </p>
            <h1>{user?.name ?? 'Operator'}</h1>
          </div>
          <span className="badge badge-muted" style={{ textTransform: 'capitalize' }}>
            {user?.roles?.[0]?.slug ?? 'user'}
          </span>
        </header>

        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
